import React, { PropsWithChildren } from 'react';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { ContentParagraph } from '../ContentParagraph';
import IConstraints from '../../../types/IContraints';

type ImageUploadDescriptionProps = {
  constraints: IConstraints;
};
export function ImageUploadDescription({ constraints, children }: PropsWithChildren<ImageUploadDescriptionProps>) {
  const {
    t,
    i18n: { language },
  } = useSonTranslation('ImageUpload');

  const minWidth = constraints?.minWidth;

  const minHeight = constraints.minHeight && constraints.minHeight > 1 && constraints.minHeight;
  const maxSize =
    (constraints?.maxFileSizeInBytes && Math.round(constraints.maxFileSizeInBytes / (1024 * 1024))) || '??';
  const format = constraints?.aspectRatio?.landscape || constraints?.aspectRatio?.portrait;
  const aspectRatio =
    format && `${format.numerator.toLocaleString(language)}:${format.denominator.toLocaleString(language)}`;

  const getConstraintDescription = () => {
    if (aspectRatio) {
      return t('uploadDescriptionWithAspectRatio', { minWidth, minHeight, maxSize, aspectRatio });
    }

    if (!minWidth && !minHeight) {
      return t('uploadDescriptionNoWidthAndHeigthConstraints', { maxSize });
    }

    if (!minHeight && minWidth) {
      return t('uploadDescriptionNoHeigthConstraints', { minWidth, maxSize });
    }

    if (!minWidth && minHeight) {
      const maxWidth = constraints.maxWidthHeightRatio?.numerator || '';
      const maxWidthHeightRatio = constraints.maxWidthHeightRatio;
      const ratio = maxWidthHeightRatio
        ? `${(maxWidthHeightRatio.numerator / maxWidthHeightRatio.denominator).toLocaleString(language, {
            maximumFractionDigits: 1,
          })}:1`
        : '';
      return t('uploadDescriptionMinHeightMaxWidth', { minHeight, maxWidth, maxSize, aspectRatio: ratio });
    }

    return t('uploadDescription', { minWidth, minHeight, maxSize });
  };

  return (
    <div className="image-upload__description-container">
      <ContentParagraph>{getConstraintDescription()}</ContentParagraph>
      <ContentParagraph>
        {/* keep whitespace here! eslint is disabled because it will remove whitespaces here. */}
        {}
        {t(`supportedPictureFormats`)} <abbr title="Joint Photographic Experts Group">JPEG</abbr>,{' '}
        <abbr title="Joint Photographic Experts Group">JPG</abbr>, <abbr title="Portable Network Graphics">PNG</abbr>.{}
      </ContentParagraph>
      {children}
    </div>
  );
}
